import { BlobStream } from '@/utils/teo-pdf/blobstream4pdf';

import { C } from './colors';
import { addArc, addRect } from './utils/paint';
import { addDivider } from './utils/structure';
import {
  addHeadline,
  addText,
  distributedTextLine,
  getBoundsOfString,
  getMaxTextWidth,
  getTextWidth,
} from './utils/text';
import { PDFDocument, type Position, type TableOptions } from './utils/types';

type TeoPDFOptions = ConstructorParameters<PDFDocument>[0] | PDFDocument;

export class TeoPDF {
  readonly doc: PDFDocument;

  #ended = false;

  constructor(options?: TeoPDFOptions) {
    this.doc = options instanceof PDFDocument ? options : new PDFDocument(options);
  }

  toBlob(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        this.end();
        const stream = this.doc.pipe(new BlobStream());
        stream.once('finish', () => {
          const blob = stream.toBlob('application/pdf');
          resolve(blob);
        });
        stream.once('error', (error) => {
          console.error('>>>>>>>>>>>>>> toBlob: error', error);
          reject(error);
        });
      } catch (error) {
        console.error('>>>>>>>>>>>>>> toBlob: error', error);
        reject(error);
      }
    });
  }

  end() {
    if (!this.#ended) {
      this.doc.end();
      this.#ended = true;
    }
    return this;
  }

  saveContext() {
    this.doc.save();
    return this;
  }

  restoreContext() {
    this.doc.restore();
    return this;
  }

  // positioning
  getPageBounds() {
    return {
      x: this.doc.page.margins.left,
      y: this.doc.page.margins.top,
      left: this.doc.page.margins.left,
      right: this.doc.page.width - this.doc.page.margins.right,
      top: this.doc.page.margins.top,
      bottom: this.doc.page.height - this.doc.page.margins.bottom,
      width: this.doc.page.width - this.doc.page.margins.left - this.doc.page.margins.right,
      height: this.doc.page.height - this.doc.page.margins.top - this.doc.page.margins.bottom,
      pageWidth: this.doc.page.width,
      pageHeight: this.doc.page.height,
    };
  }

  moveDown(lines: number) {
    this.doc.moveDown(lines);
    return this;
  }
  moveTo(pos: Position) {
    this.doc.moveTo(pos.x, pos.y);
    return this;
  }

  moveTextTo(pos: Position) {
    this.doc.x = pos.x;
    this.doc.y = pos.y;
    return this;
  }

  get position(): Position {
    return { x: this.doc.x, y: this.doc.y };
  }

  // colors
  setStrokeColor(color: string) {
    this.doc.stroke(color);
    return this;
  }

  setFillColor(color: string) {
    this.doc.fill(color);
    return this;
  }

  // alias
  setFontColor = this.setFillColor.bind(this);

  // structure
  addDivider = addDivider.bind(this);

  addSection(title: string, generateContent: (pdf: TeoPDF) => TeoPDF) {
    this.setFontColor(C.Text)
      .addHeadline(title)
      .addContent(generateContent)
      .moveDown(2.5)
      .addDivider()
      .moveDown(2.5);
  }

  addPage = (options?: Parameters<PDFDocument['addPage']>[0]) => {
    this.doc.addPage(options);
    return this;
  };

  // text
  getBoundsOfString = getBoundsOfString.bind(this);

  getTextWidth = getTextWidth.bind(this);

  getMaxTextWidth = getMaxTextWidth.bind(this);

  setFontSize(size: number) {
    this.doc.fontSize(size);
    return this;
  }

  setFont(font: string, size?: number) {
    this.doc.font(font, size);
    return this;
  }

  addHeadline = addHeadline.bind(this);

  addText = addText.bind(this);

  distributedTextLine = distributedTextLine.bind(this);

  // graphics
  addArc = addArc.bind(this);

  addRect = addRect.bind(this);

  addImage(imageData: string, pos: Position, options: PDFKit.Mixins.ImageOption) {
    this.doc.image(imageData, pos.x, pos.y, options);
    return this;
  }

  // mixed
  createTable(options: TableOptions) {
    return new TeoPDFTable(this, options).end();
  }

  createUnfinishedTable(options: TableOptions) {
    return new TeoPDFTable(this, options);
  }

  // helpers
  private addContent(generateContent: (pdf: TeoPDF) => Maybe<TeoPDF> | void) {
    return generateContent(this) ?? this;
  }
}

class TeoPDFTable {
  private readonly table: PDFKit.Mixins.PDFTableObject;

  #ended = false;

  constructor(
    private readonly pdf: TeoPDF,
    options: TableOptions,
  ) {
    this.table = this.pdf.doc.table(options);
    if (options.createRows) {
      options.createRows().forEach((row) => this.addRow(row));
    }
  }

  addRow(row: Parameters<PDFKit.Mixins.PDFTableObject['row']>[0]) {
    this.table.row(row);
    return this;
  }

  end() {
    if (!this.#ended) {
      this.table.end();
      this.#ended = true;
    }
    return this.pdf;
  }
}
